import React, { useEffect, useMemo, useState } from 'react';
import { t } from '@bay-email/core';
import { useBlock, useEditorProps, useListingPicker } from '@bay-email/editor';
import BayModal from '@bay-ui/BayModal';
import BaySpinner from '@bay-ui/BaySpinner';
import styles from './index.module.scss';

type PickerItem = {
	id: string;
	name: string;
	categoryLabel?: string;
	thumbnailUrl?: string;
	content?: any;
};

/**
 * Choose-a-Block picker. Raised when a listing primitive (Single / Multiple
 * Listings) is dropped: instead of inserting the primitive, the drop opens this
 * modal filtered to the saved blocks in the primitive's category, and the block
 * the user picks is inserted at the drop spot the primitive would have taken.
 *
 * Cancel inserts nothing — the picker is the only way to place a listing block.
 */
export function ListingPickerModal() {
	const { request, close } = useListingPicker();
	const { blockLibrary } = useEditorProps();
	const { addBlock, values } = useBlock();

	const [items, setItems] = useState<PickerItem[]>([]);
	const [loading, setLoading] = useState(false);
	const [selectedId, setSelectedId] = useState<string | null>(null);

	const fetchByCategory = blockLibrary?.fetchByCategory;
	const category = request?.category;

	useEffect(() => {
		if (!category || !fetchByCategory) {
			setItems([]);
			return;
		}

		let cancelled = false;
		setLoading(true);
		setSelectedId(null);
		Promise.resolve(fetchByCategory(category))
			.then(result => {
				if (!cancelled) setItems(result || []);
			})
			.catch(() => {
				if (!cancelled) setItems([]);
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});

		return () => {
			cancelled = true;
		};
	}, [category, fetchByCategory]);

	const selected = useMemo(
		() => items.find(item => item.id === selectedId) || null,
		[items, selectedId],
	);

	const handleInsert = () => {
		if (!request || !selected?.content?.type) return;

		// Position was already resolved to a page-root index at drop time (prepend
		// or append by the drop direction). Fall back to the end of the root if the
		// drop couldn't be pinned to a top-level block.
		const rootChildren = (values as any)?.content?.children ?? [];
		addBlock({
			type: selected.content.type,
			parentIdx: request.parentIdx,
			positionIndex: request.positionIndex ?? rootChildren.length,
			payload: selected.content,
		});
		close();
	};

	return (
		<BayModal
			show={Boolean(request)}
			size='xl'
			centered
			scrollable
			bodyClass={styles.body}
			closeButton
			title={t('Choose a Block')}
			cancelText={t('Cancel')}
			buttonText={t('Insert Block')}
			disabled={!selected?.content?.type}
			onHideFunc={close}
			submitFunc={handleInsert}
			t={t}
		>
			<p className={styles.subtitle}>{t('Pick a design to drop into your email.')}</p>

			{loading ? (
				<div className={styles.state}>
					<BaySpinner />
				</div>
			) : items.length === 0 ? (
				<div className={styles.state}>{t('No blocks in this category yet.')}</div>
			) : (
				<div className={styles.grid}>
					{items.map(item => {
						const isSelected = item.id === selectedId;
						return (
							<button
								type='button'
								key={item.id}
								className={`${styles.card}${isSelected ? ` ${styles.cardSelected}` : ''}`}
								aria-pressed={isSelected}
								onClick={() => setSelectedId(item.id)}
								onDoubleClick={handleInsert}
							>
								<div className={styles.preview}>
									{item.thumbnailUrl ? (
										<img
											className={styles.thumb}
											src={item.thumbnailUrl}
											alt={item.name}
											loading='lazy'
										/>
									) : (
										<div className={styles.thumbEmpty} />
									)}
								</div>
								<span className={styles.name}>{item.name}</span>
							</button>
						);
					})}
				</div>
			)}
		</BayModal>
	);
}
